import { format } from "date-fns";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { type CalendarView, DashboardCalendarContext } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";

function CalendarState({ children }: { children: ReactNode }) {
	const [month, setMonth] = useState(() => format(new Date(), "yyyy-MM"));
	const [pickedDate, setPickedDate] = useState<Date | null>(null);
	// Local state, not the page storage: a reload shows the month.
	const [view, setView] = useState<CalendarView>("month");
	const calendarRef = useRef<HTMLElement>(null);

	const goToMonth = useCallback((newMonth: string) => {
		setMonth(newMonth);
		setPickedDate(null);
	}, []);

	const goToDate = useCallback((date: Date) => {
		setMonth(format(date, "yyyy-MM"));
		setPickedDate(date);
	}, []);

	const scrollToCalendar = useCallback(() => {
		calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	const value = useMemo(() => ({
		month,
		pickedDate,
		view,
		calendarRef,
		goToMonth,
		goToDate,
		selectDate: setPickedDate,
		setView,
		scrollToCalendar,
	}), [month, pickedDate, view, goToMonth, goToDate, scrollToCalendar]);

	return <DashboardCalendarContext.Provider value={value}>{children}</DashboardCalendarContext.Provider>;
}

// Keyed by the dashboard user: another user starts on the current month in the month view.
export function DashboardCalendarProvider({ children }: { children: ReactNode }) {
	const user = useDashboardUser();

	return <CalendarState key={user?.id}>{children}</CalendarState>;
}
